import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import type { News, Profile } from '@/lib/supabase'

export interface AdminStats {
  totalNews: number
  publishedNews: number
  draftNews: number
  totalUsers: number
  totalComments: number
  totalViews: number
  pendingClips: number
}

export function useAdminStats() {
  const [stats, setStats] = useState<AdminStats>({
    totalNews: 0,
    publishedNews: 0,
    draftNews: 0,
    totalUsers: 0,
    totalComments: 0,
    totalViews: 0,
    pendingClips: 0,
  })
  const [loading, setLoading] = useState(true)

  const fetchStats = useCallback(async () => {
    const [newsRes, usersRes, commentsRes, clipsRes] = await Promise.all([
      supabase.from('news').select('status, views'),
      supabase.from('profiles').select('id', { count: 'exact' }),
      supabase.from('comments').select('id', { count: 'exact' }),
      supabase.from('rss_feeds').select('id', { count: 'exact' }).eq('status', 'draft'),
    ])

    const news = newsRes.data || []
    const totalViews = news.reduce((sum, n) => sum + (n.views || 0), 0)

    setStats({
      totalNews: news.length,
      publishedNews: news.filter((n) => n.status === 'publicado').length,
      draftNews: news.filter((n) => n.status === 'rascunho').length,
      totalUsers: usersRes.count || 0,
      totalComments: commentsRes.count || 0,
      totalViews,
      pendingClips: clipsRes.count || 0,
    })
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  return { stats, loading, refetch: fetchStats }
}

export function useAdminNews() {
  const [news, setNews] = useState<News[]>([])
  const [loading, setLoading] = useState(true)
  const [totalCount, setTotalCount] = useState(0)

  const fetchNews = useCallback(async (page = 1, perPage = 10, search = '', status = '') => {
    setLoading(true)
    let query = supabase.from('news').select('*', { count: 'exact' }).order('created_at', { ascending: false })
    if (search) query = query.ilike('title', `%${search}%`)
    if (status) query = query.eq('status', status)
    const from = (page - 1) * perPage
    query = query.range(from, from + perPage - 1)
    const { data, error, count } = await query
    if (error) console.error('Error fetching news:', error)
    setNews(data || [])
    setTotalCount(count || 0)
    setLoading(false)
  }, [])

  async function createNews(newsData: Partial<News>) {
    const { data, error } = await supabase.from('news').insert(newsData).select().single()
    if (error) {
      console.error('Error creating news:', error)
      return null
    }
    return data
  }

  async function updateNews(id: string, newsData: Partial<News>) {
    const { data, error } = await supabase.from('news').update(newsData).eq('id', id).select().single()
    if (error) {
      console.error('Error updating news:', error)
      return null
    }
    return data
  }

  async function deleteNews(id: string) {
    const { error } = await supabase.from('news').delete().eq('id', id)
    if (error) {
      console.error('Error deleting news:', error)
      return false
    }
    return true
  }

  async function publishNews(id: string) {
    return updateNews(id, { status: 'publicado', published_at: new Date().toISOString() })
  }

  async function unpublishNews(id: string) {
    return updateNews(id, { status: 'rascunho', published_at: null })
  }

  return { news, loading, totalCount, fetchNews, createNews, updateNews, deleteNews, publishNews, unpublishNews }
}

export function useAdminUsers() {
  const [users, setUsers] = useState<Profile[]>([])
  const [loading, setLoading] = useState(true)
  const [totalCount, setTotalCount] = useState(0)

  const fetchUsers = useCallback(async (page = 1, perPage = 10, search = '', role = '') => {
    setLoading(true)
    let query = supabase.from('profiles').select('*', { count: 'exact' }).order('created_at', { ascending: false })
    if (search) query = query.ilike('name', `%${search}%`)
    if (role) query = query.eq('role', role)
    const from = (page - 1) * perPage
    query = query.range(from, from + perPage - 1)
    const { data, error, count } = await query
    if (error) console.error('Error fetching users:', error)
    setUsers(data || [])
    setTotalCount(count || 0)
    setLoading(false)
  }, [])

  async function updateUserRole(id: string, role: string) {
    const { error } = await supabase.from('profiles').update({ role }).eq('id', id)
    if (error) {
      console.error('Error updating role:', error)
      return false
    }
    return true
  }

  async function toggleBanUser(id: string, banned: boolean) {
    const { error } = await supabase.from('profiles').update({ banned }).eq('id', id)
    if (error) {
      console.error('Error banning user:', error)
      return false
    }
    return true
  }

  return { users, loading, totalCount, fetchUsers, updateUserRole, toggleBanUser }
}

interface HighlightRow {
  id: string
  news_id: string
  type: string
  category: string | null
  display_order: number | null
  created_at: string
  news_title?: string
}

export function useAdminHighlights() {
  const [highlights, setHighlights] = useState<HighlightRow[]>([])
  const [loading, setLoading] = useState(true)

  const fetchHighlights = useCallback(async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('highlights')
      .select('*, news(title)')
      .order('display_order', { ascending: true })
    if (error) {
      console.error('Error fetching highlights:', error)
    } else {
      const mapped: HighlightRow[] = (data || []).map((h: Record<string, unknown>) => ({
        id: h.id as string,
        news_id: h.news_id as string,
        type: h.type as string,
        category: h.category as string | null,
        display_order: h.display_order as number | null,
        created_at: h.created_at as string,
        news_title: (h.news as Record<string, unknown>)?.title as string | undefined,
      }))
      setHighlights(mapped)
    }
    setLoading(false)
  }, [])

  async function createHighlight(highlight: { news_id: string; type: string; category?: string; display_order?: number }) {
    const { data, error } = await supabase.from('highlights').insert(highlight).select().single()
    if (error) {
      console.error('Error creating highlight:', error)
      return null
    }
    return data
  }

  async function deleteHighlight(id: string) {
    const { error } = await supabase.from('highlights').delete().eq('id', id)
    if (error) {
      console.error('Error deleting highlight:', error)
      return false
    }
    return true
  }

  async function updateHighlightOrder(id: string, display_order: number) {
    const { error } = await supabase.from('highlights').update({ display_order }).eq('id', id)
    if (error) {
      console.error('Error updating highlight:', error)
      return false
    }
    return true
  }

  return { highlights, loading, fetchHighlights, createHighlight, deleteHighlight, updateHighlightOrder }
}
